import React, { useEffect, useRef, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import mixpanel from 'mixpanel-browser';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { COLOR_E, exchangeRates, sampleMarket } from 'src/const';
import { getDistrictInfo, getMarkers } from 'src/store/Dashboard/actions';
import { Alert, Card, CardContent, CircularProgress, Skeleton, Typography } from '@mui/material';
import {
  COLOR_ERROR_1,
  COLOR_ERROR_BACKGROUNG,
  COLOR_GREY_200,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_1_300,
  COLOR_WARNING,
} from 'src/colors';
import GranularDataCard from '../HOC/Cards/GranularDataCard';

mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');

const HistoricalModal = props => {
  const { open, historicalChartTitle, onClose, currency, handleOnSampleClick } = props;

  const isUserAllowed = useSelector(state => (state as any).dashboard.isUserAllowed);
  const historicalData = useSelector(state => (state as any).dashboard.historicalData);
  const user = useSelector(state => (state as any).dashboard.user);
  const [dailyData, setDailyData] = useState<any>(null);
  const [weeklyData, setWeeklyData] = useState<any>(null);
  const [monthlyData, setMonthlyData] = useState<any>(null);
  const [isMarketLocked, setIsMarketLocked] = useState<boolean>(!isUserAllowed);
  const [isDisabled, setisDisabled] = useState<boolean>(false);
  const [isUserInfoLoaded, setIsUserInfoLoaded] = useState<boolean>(false);

  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setIsMarketLocked(!isUserAllowed);
  }, [isUserAllowed]);

  useEffect(() => {
    if (historicalData) {
      setMonthlyData(historicalData);
    }
  }, [historicalData]);

  useEffect(() => {
    user.email !== 'Trial' && setIsUserInfoLoaded(true);
  }, [user]);

  useEffect(() => {
    if (isDisabled) {
      window.scrollTo(0, 0);
    }
  }, [isDisabled]);
  return (
    <>
      <Modal
        open={open}
        onClose={() => onClose(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: isMobileScreen ? '90vh' : '95vh',
            width: isMobileScreen ? '95vw' : '95vw',
            backgroundColor: 'transparent',
            borderRadius: '20px',
            position: 'relative',
            overflow: 'hidden',
            outline: 'none',
            padding: '0px',
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: '10px',
              right: '9px',
              zIndex: 10000,
              padding: '0px',
              borderRadius: '500px',
              boxShadow:
                '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
              backgroundColor: '#fff',
              color: COLOR_ERROR_1,
              '-webkit-background-color': '#fff',
              '-webkit-color': COLOR_ERROR_1,
              minWidth: '40px',
              overflow: 'hidden',
              '&:hover': {
                backgroundColor: '#fff !important',
              },
            }}
          >
            <CloseIcon
              fontSize="large"
              sx={{
                height: '35px',
                width: '35px',
                color: COLOR_ERROR_1,
                '-webkit-color': COLOR_ERROR_1,
                '&:hover': {
                  backgroundColor: COLOR_ERROR_BACKGROUNG,
                  transition: 'background-color 0.3s ease-in-out',
                },
              }}
            />
          </Button>

          <Box sx={{ height: '100%', width: '100%' }}>
            {!historicalData && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: '#fff',
                  height: '40vh',
                  width: '60vh',
                  borderRadius: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CircularProgress />
              </div>
            )}

            {(historicalChartTitle === 'Revenue' ||
              historicalChartTitle === 'Previous Month Revenue') && (
              <GranularDataCard
                metric={'revenue'}
                name={'Revenue'}
                dailyData={dailyData}
                weeklyData={weeklyData}
                monthlyData={monthlyData}
                unit={{ symbol: exchangeRates[currency].symbol, isCurrency: true }}
                isMarketLocked={isMarketLocked}
                currency={currency}
                showNoFilter={true}
                isApiFetching={!historicalData}
                isMobileScreen={isMobileScreen}
                isDisabled={isDisabled}
                handleOnSampleClick={handleOnSampleClick}
              />
            )}
            {(historicalChartTitle === 'Occupancy Rate' ||
              historicalChartTitle === 'Previous Month Occupancy Rate') && (
              <GranularDataCard
                metric={'occupancy_rate'}
                name={'Occupancy Rate'}
                dailyData={dailyData}
                weeklyData={weeklyData}
                monthlyData={monthlyData}
                unit={{ symbol: '%', isCurrency: false }}
                isMarketLocked={isMarketLocked}
                currency={currency}
                isApiFetching={!historicalData}
                isMobileScreen={isMobileScreen}
                isDisabled={isDisabled}
                handleOnSampleClick={handleOnSampleClick}
              />
            )}
            {historicalChartTitle === 'Nightly Rate' && (
              <GranularDataCard
                metric={'daily_rate'}
                name={'Average Daily Rate'}
                dailyData={dailyData}
                weeklyData={weeklyData}
                monthlyData={monthlyData}
                unit={{ symbol: exchangeRates[currency].symbol, isCurrency: true }}
                isMarketLocked={isMarketLocked}
                currency={currency}
                showNoFilter={true}
                isApiFetching={!historicalData}
                isMobileScreen={isMobileScreen}
                isDisabled={isDisabled}
                handleOnSampleClick={handleOnSampleClick}
              />
            )}

            {isMarketLocked && (
              <>
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100vh',
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    backdropFilter: 'blur(4px)',
                  }}
                />
                <div
                  style={{
                    position: 'fixed',
                    top: '50%',
                    left: '45%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '1rem',
                    flexDirection: 'column',
                    color: COLOR_E,
                  }}
                >
                  <Button
                    onClick={() => {
                      setIsMarketLocked(false);
                      setisDisabled(true);
                    }}
                    style={{ all: 'unset', cursor: 'pointer' }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      View Demo Data
                    </Typography>
                  </Button>
                </div>
              </>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default HistoricalModal;
